const DEFAULT_OMISSION = '...';

/**
 * `slice`, but never between the halves of a surrogate pair — cutting one in
 * two leaves a lone surrogate that renders as a replacement character.
 */
function sliceWholeUnits(text: string, end: number): string {
  const splitsPair =
    end > 0 &&
    end < text.length &&
    text.charCodeAt(end - 1) >= 0xd800 &&
    text.charCodeAt(end - 1) <= 0xdbff;

  return text.slice(0, splitsPair ? end - 1 : end);
}

type TruncateHtmlOptions = {
  /** Maximum length of the rendered text, including the omission. */
  length: number;
  omission?: string;
};

/**
 * Truncates HTML by rendered text length, keeping the markup valid.
 *
 * The markup is parsed into an inert `<template>`, so raw HTML can go in, but
 * the output is re-serialized markup: always sanitize it afterwards. Sanitize
 * the input too, or text the sanitizer would drop (a `<script>` body) spends
 * the budget.
 *
 * Unlike `lodash/truncate`, tags and attributes don't consume the budget — and
 * so can't be cut through, which would make the browser drop the whole tag and
 * take its text with it.
 */
export function truncateHtml(
  html: string,
  { length, omission = DEFAULT_OMISSION }: TruncateHtmlOptions,
): string {
  if (!html) return html;

  // <template>, not <div>: it parses table fragments without dropping them.
  const template = document.createElement('template');
  template.innerHTML = html;

  if ((template.content.textContent ?? '').length <= length) return html;

  // A shorter budget than the omission would push the result back over
  // `length`, so the omission itself is clamped first — and clamping it can
  // split a pair just as truncating the text can.
  const suffix = sliceWholeUnits(omission, length);
  const budget = length - suffix.length;
  let used = 0;
  let truncated = false;

  const walk = (node: Node): void => {
    Array.from(node.childNodes).forEach((child) => {
      if (truncated) {
        child.parentNode?.removeChild(child);
        return;
      }

      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.nodeValue ?? '';

        if (used + text.length <= budget) {
          used += text.length;
          return;
        }

        child.nodeValue = `${sliceWholeUnits(text, budget - used)}${suffix}`;
        used = budget;
        truncated = true;
        return;
      }

      walk(child);
    });
  };

  walk(template.content);

  return template.innerHTML;
}
